import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage, db } from '../../firebase/firebaseConfig';
import { compressImage } from '../../utils/imageUtils';
import PaymentMethodsForm from './PaymentMethodsForm';
import PackageSelectionModal from './PackageSelectionModal';
import VideoCallSettings from './VideoCallSettings';
import StoreVerificationForm from './StoreVerificationForm';
import VerificationBadge from './VerificationBadge';
import { PACKAGE_TYPES, PACKAGES } from '../../data/packageModels';
import { useMarketplace } from '../../context/MarketplaceContext';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';

const StoreSettingsForm = ({ store, onUpdate }) => {
  const { t } = useTranslation();
  const { subscribeToPackage, getStoreSubscription } = useMarketplace();
  const [loading, setLoading] = useState(false);
  const [showPackageModal, setShowPackageModal] = useState(false);
  const [currentPackage, setCurrentPackage] = useState(PACKAGE_TYPES.FREE);
  const [subscriptionHistory, setSubscriptionHistory] = useState([]);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [showVerificationForm, setShowVerificationForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: '',
    contact: {
      phone: '',
      email: '',
      lineId: '',
      address: ''
    },
    logo: '',
    coverImage: '',
    payment: {
      methods: ['bank_transfer'],
      bankAccounts: [],
      qrCodes: [],
      cashOnPickup: { enabled: false, location: '', time: '' },
      cashOnDelivery: { enabled: false, courierCompanies: [] }
    },
    shipping: {
      methods: [],
      courierCompanies: [],
      pickupLocation: '',
      pickupTime: '',
      specialVehicleInfo: '',
      fees: {
        courier: { type: 'fixed', amount: 0 },
        pickup: { type: 'fixed', amount: 0 },
        specialVehicle: { type: 'fixed', amount: 0 }
      }
    }
  });

  // Load subscription history
  useEffect(() => {
    if (store?.id) {
      loadSubscriptionHistory();
    }
  }, [store?.id]);

  const loadSubscriptionHistory = async () => {
    if (!store?.id) return;
    
    try {
      // Load from storeSubscriptions collection
      const subscriptionsRef = collection(db, 'storeSubscriptions');
      
      // Try with orderBy first (requires index)
      let snapshot;
      try {
        const q = query(
          subscriptionsRef,
          where('storeId', '==', store.id),
          orderBy('startDate', 'desc')
        );
        snapshot = await getDocs(q);
      } catch (indexError) {
        // If index error, try without orderBy
        console.warn('Index not found, loading without orderBy:', indexError);
        const q = query(
          subscriptionsRef,
          where('storeId', '==', store.id)
        );
        snapshot = await getDocs(q);
      }
      const history = snapshot.docs.map(doc => {
        const data = doc.data();
        // Convert Firestore Timestamp to Date properly
        let startDate = null;
        let endDate = null;
        let createdAt = null;
        
        if (data.startDate) {
          try {
            if (data.startDate.toDate && typeof data.startDate.toDate === 'function') {
              startDate = data.startDate.toDate();
            } else if (data.startDate instanceof Date) {
              startDate = data.startDate;
            } else if (data.startDate.seconds) {
              // Firestore Timestamp format { seconds, nanoseconds }
              startDate = new Date(data.startDate.seconds * 1000);
            } else if (typeof data.startDate === 'string' || typeof data.startDate === 'number') {
              startDate = new Date(data.startDate);
            }
            // Validate date
            if (startDate && isNaN(startDate.getTime())) {
              console.warn('Invalid startDate:', data.startDate);
              startDate = new Date(); // Fallback to current date
            }
          } catch (e) {
            console.error('Error parsing startDate:', e);
            startDate = new Date(); // Fallback to current date
          }
        } else {
          startDate = new Date(); // Default to current date if missing
        }
        
        if (data.endDate) {
          try {
            if (data.endDate.toDate && typeof data.endDate.toDate === 'function') {
              endDate = data.endDate.toDate();
            } else if (data.endDate instanceof Date) {
              endDate = data.endDate;
            } else if (data.endDate.seconds) {
              // Firestore Timestamp format { seconds, nanoseconds }
              endDate = new Date(data.endDate.seconds * 1000);
            } else if (typeof data.endDate === 'string' || typeof data.endDate === 'number') {
              endDate = new Date(data.endDate);
            }
            // Validate date
            if (endDate && isNaN(endDate.getTime())) {
              console.warn('Invalid endDate:', data.endDate);
              endDate = null;
            }
          } catch (e) {
            console.error('Error parsing endDate:', e);
            endDate = null;
          }
        }
        
        if (data.createdAt) {
          if (data.createdAt.toDate) {
            createdAt = data.createdAt.toDate();
          } else if (data.createdAt instanceof Date) {
            createdAt = data.createdAt;
          } else if (typeof data.createdAt === 'string' || typeof data.createdAt === 'number') {
            createdAt = new Date(data.createdAt);
          }
        }
        
        const result = {
          id: doc.id,
          ...data,
          startDate: startDate || new Date(),
          endDate: endDate || null,
          createdAt: createdAt || new Date()
        };
        
        console.log('📅 Converted subscription:', {
          id: result.id,
          packageType: result.packageType,
          startDate: result.startDate,
          endDate: result.endDate,
          startDateValid: result.startDate && !isNaN(result.startDate.getTime()),
          endDateValid: result.endDate ? !isNaN(result.endDate.getTime()) : true
        });
        
        return result;
      });
      
      // Sort by startDate descending (if not already sorted by query)
      history.sort((a, b) => {
        const aDate = a.startDate?.getTime() || 0;
        const bDate = b.startDate?.getTime() || 0;
        return bDate - aDate;
      });

      // Also check store document for packageType (if no subscription history)
      if (history.length === 0 && store.packageType) {
        history.push({
          id: 'store-package',
          packageType: store.packageType,
          status: 'active',
          startDate: store.openedAt ? new Date(store.openedAt) : new Date(),
          endDate: null,
          autoRenew: false,
          paymentMethod: 'free',
          transactionId: 'store-creation'
        });
      }

      setSubscriptionHistory(history);
      
      // Find current active subscription
      const active = history.find(sub => sub.status === 'active') || history[0];
      setCurrentSubscription(active);
    } catch (error) {
      console.error('Error loading subscription history:', error);
      // If query fails (no index), try to get from store document
      if (store.packageType) {
        setSubscriptionHistory([{
          id: 'store-package',
          packageType: store.packageType,
          status: 'active',
          startDate: store.openedAt ? new Date(store.openedAt) : new Date(),
          endDate: null,
          autoRenew: false,
          paymentMethod: 'free',
          transactionId: 'store-creation'
        }]);
        setCurrentSubscription({
          packageType: store.packageType,
          status: 'active',
          startDate: store.openedAt ? new Date(store.openedAt) : new Date(),
          endDate: null
        });
      }
    }
  };

  // Load current package subscription
  useEffect(() => {
    const loadSubscription = async () => {
      if (store && store.id) {
        try {
          console.log('Loading subscription for store:', store.id);
          const subscription = await getStoreSubscription(store.id);
          console.log('Subscription data:', subscription);
          const packageType = subscription.packageType || store.packageType || PACKAGE_TYPES.FREE;
          console.log('Setting current package to:', packageType);
          setCurrentPackage(packageType);
        } catch (error) {
          console.error('Error loading subscription:', error);
          setCurrentPackage(store.packageType || PACKAGE_TYPES.FREE);
        }
      } else if (store && store.packageType) {
        console.log('Using store.packageType:', store.packageType);
        setCurrentPackage(store.packageType);
      } else if (!store) {
        setCurrentPackage(PACKAGE_TYPES.FREE);
      }
    };
    loadSubscription();
  }, [store?.id, store?.packageType, getStoreSubscription]);

  useEffect(() => {
    if (store) {
      setFormData({
        name: store.name || '',
        description: store.description || '',
        category: store.category || '',
        contact: {
          phone: store.contact?.phone || '',
          email: store.contact?.email || '',
          lineId: store.contact?.lineId || '',
          address: store.contact?.address || ''
        },
        logo: store.logo || '',
        coverImage: store.coverImage || '',
        payment: {
          methods: store.payment?.methods || ['bank_transfer'],
          bankAccounts: store.payment?.bankAccounts || [],
          qrCodes: store.payment?.qrCodes || [],
          cashOnPickup: store.payment?.cashOnPickup || { enabled: false, location: '', time: '' },
          cashOnDelivery: store.payment?.cashOnDelivery || { enabled: false, courierCompanies: [] }
        },
        shipping: {
          methods: store.shipping?.methods || [],
          courierCompanies: store.shipping?.courierCompanies || [],
          pickupLocation: store.shipping?.pickupLocation || '',
          pickupTime: store.shipping?.pickupTime || '',
          specialVehicleInfo: store.shipping?.specialVehicleInfo || '',
          fees: {
            courier: store.shipping?.fees?.courier || { type: 'fixed', amount: 0 },
            pickup: store.shipping?.fees?.pickup || { type: 'fixed', amount: 0 },
            specialVehicle: store.shipping?.fees?.specialVehicle || { type: 'fixed', amount: 0 }
          }
        }
      });
    }
  }, [store]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name.startsWith('contact.')) {
      const contactField = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        contact: {
          ...prev.contact,
          [contactField]: value
        }
      }));
    } else if (name.startsWith('payment.')) {
      const parts = name.split('.');
      if (parts.length === 2) {
        const paymentField = parts[1];
        setFormData(prev => ({
          ...prev,
          payment: {
            ...prev.payment,
            [paymentField]: value
          }
        }));
      }
    } else if (name.startsWith('shipping.')) {
      const parts = name.split('.');
      if (parts.length === 2) {
        const shippingField = parts[1];
        setFormData(prev => ({
          ...prev,
          shipping: {
            ...prev.shipping,
            [shippingField]: value
          }
        }));
      } else if (parts.length === 3 && parts[1] === 'fees') {
        const feeType = parts[2];
        setFormData(prev => ({
          ...prev,
          shipping: {
            ...prev.shipping,
            fees: {
              ...prev.shipping.fees,
              [feeType]: {
                ...prev.shipping.fees[feeType],
                amount: parseFloat(value) || 0
              }
            }
          }
        }));
      } else if (parts.length === 4 && parts[1] === 'fees') {
        const feeType = parts[2];
        const feeField = parts[3];
        setFormData(prev => ({
          ...prev,
          shipping: {
            ...prev.shipping,
            fees: {
              ...prev.shipping.fees,
              [feeType]: {
                ...prev.shipping.fees[feeType],
                [feeField]: value
              }
            }
          }
        }));
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleImageUpload = async (file, type) => {
    try {
      setLoading(true);
      
      // Validate file
      if (!file.type.startsWith('image/')) {
        throw new Error(t('store.fileMustBeImage'));
      }
      if (file.size > 5 * 1024 * 1024) {
        throw new Error(t('store.fileTooLarge'));
      }
      
      // Compress image
      let fileToUpload = file;
      try {
        if (type === 'logo') {
          fileToUpload = await compressImage(file, 400, 400, 0.9);
        } else if (type === 'payment.qrCode') {
          fileToUpload = await compressImage(file, 400, 400, 0.9);
        } else {
          fileToUpload = await compressImage(file, 1200, 400, 0.85);
        }
      } catch (compressError) {
        console.warn('Error compressing image, using original:', compressError);
        fileToUpload = file;
      }
      
      // Upload to Firebase Storage
      const timestamp = Date.now();
      const randomString = Math.random().toString(36).substring(2, 15);
      const fileType = type === 'payment.qrCode' ? 'qrCode' : type;
      const fileName = `stores/${fileType}_${timestamp}_${randomString}.jpg`;
      const storageRef = ref(storage, fileName);
      
      await uploadBytes(storageRef, fileToUpload);
      const downloadURL = await getDownloadURL(storageRef);
      
      if (type === 'payment.qrCode') {
        setFormData(prev => ({
          ...prev,
          payment: {
            ...prev.payment,
            qrCode: downloadURL
          }
        }));
      } else {
        setFormData(prev => ({
          ...prev,
          [type]: downloadURL
        }));
      }
      
      return downloadURL;
    } catch (error) {
      console.error('Error uploading image:', error);
      alert(t('store.uploadError') + error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      alert(t('store.pleaseEnterStoreName'));
      return;
    }
    
    setLoading(true);
    try {
      await onUpdate(formData);
    } catch (error) {
      console.error('Error updating store:', error);
    } finally {
      setLoading(false);
    }
  };

  const categories = [
    { value: '', label: t('store.selectCategory') },
    { value: 'Flowers', label: 'Flowers' },
    { value: 'Seeds', label: 'Seeds' },
    { value: 'Edibles', label: 'Edibles' },
    { value: 'Pre Rolls', label: 'Pre Rolls' },
    { value: 'Cannabis Oils', label: 'Cannabis Oils' },
    { value: 'CBD Products', label: 'CBD Products' }
  ];

  const handlePackageSelect = async (packageType) => {
    if (!store || !store.id) {
      alert(t('store.storeNotFound'));
      return;
    }

    try {
      setLoading(true);
      console.log('Subscribing to package:', packageType, 'for store:', store.id);
      const result = await subscribeToPackage(store.id, packageType);
      console.log('Subscribe result:', result);
      
      if (result && result.success) {
        // อัพเดท currentPackage state ทันที
        console.log('Setting current package to:', packageType);
        setCurrentPackage(packageType);
        
        setShowPackageModal(false);
        alert(t('store.packageSubscribeSuccess', { packageName: PACKAGES[packageType].name }));
        
        // รอสักครู่เพื่อให้ Firestore อัพเดทข้อมูล
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Refresh store data โดยเรียก onUpdate เพื่อ trigger fetchStore ใน parent
        // ส่ง packageType เพื่อให้ parent รู้ว่าเป็นการอัพเดท Package
        if (onUpdate) {
          // สร้าง store object ใหม่ที่มี packageType อัพเดท
          const updatedStoreData = {
            ...formData,
            packageType: packageType
          };
          await onUpdate(updatedStoreData);
        }
        
        // Force reload subscription and history
        try {
          const updatedSubscription = await getStoreSubscription(store.id);
          console.log('Updated subscription after subscribe:', updatedSubscription);
          if (updatedSubscription && updatedSubscription.packageType) {
            setCurrentPackage(updatedSubscription.packageType);
          }
          // Reload subscription history
          await loadSubscriptionHistory();
        } catch (error) {
          console.error('Error reloading subscription:', error);
        }
      }
    } catch (error) {
      console.error('Error subscribing to package:', error);
      alert(t('store.packageSubscribeError') + error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-800">{t('store.settings')}</h2>
        <button
          onClick={() => setShowPackageModal(true)}
          className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white font-semibold rounded-lg transition-colors"
        >
          📦 {t('store.managePackage')}
        </button>
      </div>

      {/* Current Package Display */}
      <div className="mb-6 p-4 bg-gradient-to-r from-green-50 to-blue-50 rounded-lg border border-green-200">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-600 mb-1">{t('store.currentPackage')}</p>
            <p className="text-xl font-bold text-gray-800">
              {PACKAGES[currentPackage]?.name || t('store.freePackage')}
            </p>
            {currentSubscription?.endDate && (
              <p className="text-xs text-gray-500 mt-1">
                หมดอายุ: {currentSubscription.endDate.toLocaleDateString('th-TH', { 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}
              </p>
            )}
            {!currentSubscription?.endDate && currentPackage !== PACKAGE_TYPES.FREE && (
              <p className="text-xs text-green-600 mt-1">ใช้งานได้ไม่จำกัด</p>
            )}
          </div>
          {currentPackage !== PACKAGE_TYPES.FREE && (
            <div className="text-right">
              <p className="text-sm text-gray-600">{t('store.price')}</p>
              <p className="text-lg font-bold text-green-600">
                ฿{PACKAGES[currentPackage]?.price.toLocaleString()}/เดือน
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Store Verification Section */}
      <div className="mb-6 p-4 bg-white rounded-lg border border-gray-200 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
              <span>✅</span>
              <span>การตรวจสอบร้านค้า</span>
            </h3>
            {store?.verification && (
              <VerificationBadge verification={store.verification} size="sm" />
            )}
          </div>
          <button
            onClick={() => setShowVerificationForm(true)}
            className="px-4 py-2 bg-green-500 hover:bg-green-600 text-white font-semibold rounded-lg transition-colors text-sm"
          >
            {store?.verification ? '📝 แก้ไข/ต่ออายุ' : '📤 ส่งเอกสารให้แอดมินตรวจสอบ'}
          </button>
        </div>
        <p className="text-sm text-gray-600">
          {store?.verification?.status === 'approved' 
            ? 'ร้านค้าของคุณได้รับการตรวจสอบแล้ว ✅' 
            : store?.verification?.status === 'pending' || store?.verification?.status === 'in_review'
            ? 'เอกสารของคุณอยู่ระหว่างการตรวจสอบโดยแอดมิน ⏳'
            : 'ส่งเอกสารเพื่อให้แอดมินตรวจสอบร้านค้าของคุณ'}
        </p>
      </div>

      {/* Subscription History Timeline */}
      {subscriptionHistory.length > 0 && (
        <div className="mb-6 p-4 bg-white rounded-lg border border-gray-200 shadow-sm">
          <button
            onClick={() => setShowHistory(!showHistory)}
            className="w-full flex items-center justify-between text-left mb-4"
          >
            <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
              <span>📅</span>
              <span>ประวัติการสมัคร Package</span>
              <span className="text-sm text-gray-500 font-normal">
                ({subscriptionHistory.length} รายการ)
              </span>
            </h3>
            <svg
              className={`w-5 h-5 text-gray-600 transition-transform ${showHistory ? 'transform rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          
          {showHistory && (
            <div className="space-y-4">
            {subscriptionHistory.map((sub, index) => {
              const isActive = sub.status === 'active';
              const isExpired = sub.endDate && new Date(sub.endDate) < new Date();
              const packageInfo = PACKAGES[sub.packageType] || PACKAGES[PACKAGE_TYPES.FREE];
              
              return (
                <div 
                  key={sub.id} 
                  className={`relative pl-8 pb-4 ${index < subscriptionHistory.length - 1 ? 'border-l-2 border-gray-200' : ''}`}
                >
                  {/* Timeline Dot */}
                  <div className={`absolute left-0 top-1 w-4 h-4 rounded-full border-2 ${
                    isActive 
                      ? 'bg-green-500 border-green-600' 
                      : isExpired 
                        ? 'bg-red-500 border-red-600' 
                        : 'bg-gray-400 border-gray-500'
                  }`}></div>
                  
                  {/* Content */}
                  <div className={`p-3 rounded-lg ${
                    isActive 
                      ? 'bg-green-50 border border-green-200' 
                      : 'bg-gray-50 border border-gray-200'
                  }`}>
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-bold text-gray-800">{packageInfo.name}</span>
                          {isActive && (
                            <span className="px-2 py-0.5 bg-green-500 text-white text-xs rounded-full">
                              ใช้งานอยู่
                            </span>
                          )}
                          {isExpired && (
                            <span className="px-2 py-0.5 bg-red-500 text-white text-xs rounded-full">
                              หมดอายุแล้ว
                            </span>
                          )}
                          {sub.status === 'cancelled' && (
                            <span className="px-2 py-0.5 bg-gray-500 text-white text-xs rounded-full">
                              ยกเลิกแล้ว
                            </span>
                          )}
                        </div>
                        
                        <div className="text-sm text-gray-600 space-y-1">
                          <p>
                            <span className="font-medium">วันที่สมัคร:</span>{' '}
                            {(() => {
                              let dateToShow = null;
                              if (sub.startDate) {
                                try {
                                  if (sub.startDate instanceof Date && !isNaN(sub.startDate.getTime())) {
                                    dateToShow = sub.startDate;
                                  } else if (sub.startDate.toDate && typeof sub.startDate.toDate === 'function') {
                                    dateToShow = sub.startDate.toDate();
                                  } else if (sub.startDate.seconds) {
                                    dateToShow = new Date(sub.startDate.seconds * 1000);
                                  } else if (typeof sub.startDate === 'string' || typeof sub.startDate === 'number') {
                                    dateToShow = new Date(sub.startDate);
                                  }
                                } catch (e) {
                                  console.error('Error parsing startDate:', e, sub.startDate);
                                }
                              }
                              
                              return dateToShow && !isNaN(dateToShow.getTime()) ? (
                                <span className="text-gray-800">
                                  {dateToShow.toLocaleDateString('th-TH', { 
                                    year: 'numeric', 
                                    month: 'long', 
                                    day: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  })}
                                </span>
                              ) : (
                                <span className="text-red-500">ไม่พบข้อมูล</span>
                              );
                            })()}
                          </p>
                          
                          {(() => {
                            let endDateToShow = null;
                            if (sub.endDate) {
                              try {
                                if (sub.endDate instanceof Date && !isNaN(sub.endDate.getTime())) {
                                  endDateToShow = sub.endDate;
                                } else if (sub.endDate.toDate && typeof sub.endDate.toDate === 'function') {
                                  endDateToShow = sub.endDate.toDate();
                                } else if (sub.endDate.seconds) {
                                  endDateToShow = new Date(sub.endDate.seconds * 1000);
                                } else if (typeof sub.endDate === 'string' || typeof sub.endDate === 'number') {
                                  endDateToShow = new Date(sub.endDate);
                                }
                              } catch (e) {
                                console.error('Error parsing endDate:', e, sub.endDate);
                              }
                            }
                            
                            return endDateToShow && !isNaN(endDateToShow.getTime()) ? (
                              <p>
                                <span className="font-medium">วันที่สิ้นสุด:</span>{' '}
                                <span className={isExpired ? 'text-red-600 font-semibold' : 'text-gray-800'}>
                                  {endDateToShow.toLocaleDateString('th-TH', { 
                                    year: 'numeric', 
                                    month: 'long', 
                                    day: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  })}
                                </span>
                              </p>
                            ) : sub.endDate === null ? (
                              <p className="text-green-600 font-medium">ใช้งานได้ไม่จำกัด (ไม่มีวันสิ้นสุด)</p>
                            ) : (
                              <p className="text-gray-500">ยังไม่กำหนดวันสิ้นสุด</p>
                            );
                          })()}
                          
                          {sub.paymentMethod && (
                            <p>
                              <span className="font-medium">ชำระด้วย:</span>{' '}
                              {sub.paymentMethod === 'bonus-coins' || sub.transactionId === 'free-trial-promotion' ? 'เหรียญโบนัส' : 
                               sub.paymentMethod === 'tcoins' ? 'T Coins' : 
                               sub.paymentMethod === 'bank' ? 'โอนเงิน' :
                               sub.paymentMethod === 'free' ? 'ฟรี' :
                               sub.paymentMethod}
                            </p>
                          )}
                        </div>
                      </div>
                      
                      {sub.packageType !== PACKAGE_TYPES.FREE && (
                        <div className="text-right">
                          <p className="text-sm text-gray-500">ราคา</p>
                          <p className="text-lg font-bold text-green-600">
                            ฿{packageInfo.price?.toLocaleString() || '0'}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
            </div>
          )}
        </div>
      )}
      
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* ข้อมูลพื้นฐาน */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-700 border-b pb-2">{t('store.basicInfo')}</h3>
          
          {/* ชื่อร้าน */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              {t('store.storeNameRequired')} <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              required
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
              placeholder={t('store.enterStoreName')}
            />
          </div>

          {/* คำอธิบายร้าน */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              {t('store.storeDescription')}
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              rows="4"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
              placeholder={t('store.storeDescriptionPlaceholder')}
            />
          </div>

          {/* หมวดหมู่ */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              {t('store.productCategory')}
            </label>
            <select
              name="category"
              value={formData.category}
              onChange={handleInputChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
            >
              {categories.map(cat => (
                <option key={cat.value} value={cat.value}>
                  {cat.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* รูปภาพ */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-700 border-b pb-2">{t('store.images')}</h3>
          
          {/* Logo */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              {t('store.storeLogo')}
            </label>
            <div className="flex items-center gap-4">
              {formData.logo && (
                <img
                  src={formData.logo}
                  alt="Store Logo"
                  className="w-24 h-24 object-cover rounded-lg border border-gray-300"
                />
              )}
              <div className="flex-1">
                <input
                  type="file"
                  accept="image/*"
                  onChange={async (e) => {
                    const file = e.target.files[0];
                    if (file) {
                      try {
                        await handleImageUpload(file, 'logo');
                        alert(t('store.logoUploadSuccess'));
                      } catch (error) {
                        console.error('Error uploading logo:', error);
                      }
                    }
                  }}
                  className="w-full text-sm text-gray-600 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-green-50 file:text-green-700 hover:file:bg-green-100"
                />
                <p className="text-xs text-gray-500 mt-1">{t('store.logoRecommendation')}</p>
              </div>
            </div>
          </div>

          {/* Cover Image */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              {t('store.coverImage')}
            </label>
            <div className="flex items-center gap-4">
              {formData.coverImage && (
                <img
                  src={formData.coverImage}
                  alt="Store Cover"
                  className="w-32 h-20 object-cover rounded-lg border border-gray-300"
                />
              )}
              <div className="flex-1">
                <input
                  type="file"
                  accept="image/*"
                  onChange={async (e) => {
                    const file = e.target.files[0];
                    if (file) {
                      try {
                        await handleImageUpload(file, 'coverImage');
                        alert(t('store.coverUploadSuccess'));
                      } catch (error) {
                        console.error('Error uploading cover image:', error);
                      }
                    }
                  }}
                  className="w-full text-sm text-gray-600 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-green-50 file:text-green-700 hover:file:bg-green-100"
                />
                <p className="text-xs text-gray-500 mt-1">{t('store.coverRecommendation')}</p>
              </div>
            </div>
          </div>
        </div>

        {/* ข้อมูลติดต่อ */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-700 border-b pb-2">{t('store.contactInfo')}</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* เบอร์โทรศัพท์ */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                {t('store.phone')}
              </label>
              <input
                type="tel"
                name="contact.phone"
                value={formData.contact.phone}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                placeholder="02-123-4567"
              />
            </div>

            {/* อีเมล */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                {t('store.email')}
              </label>
              <input
                type="email"
                name="contact.email"
                value={formData.contact.email}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                placeholder="store@example.com"
              />
            </div>

            {/* Line ID */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Line ID
              </label>
              <input
                type="text"
                name="contact.lineId"
                value={formData.contact.lineId}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                placeholder="@storeline"
              />
            </div>
          </div>

          {/* ที่อยู่ */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              {t('store.address')}
            </label>
            <textarea
              name="contact.address"
              value={formData.contact.address}
              onChange={handleInputChange}
              rows="3"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
              placeholder={t('store.addressPlaceholder')}
            />
          </div>
        </div>

        {/* ข้อมูลการชำระเงิน */}
        <PaymentMethodsForm
          payment={formData.payment}
          packageType={store?.packageType || PACKAGE_TYPES.FREE}
          onChange={(newPayment) => {
            setFormData(prev => ({
              ...prev,
              payment: newPayment
            }));
          }}
        />

        {/* ข้อมูลการจัดส่ง */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-700 border-b pb-2">{t('store.supportedShipping')}</h3>
          
          {/* บริษัทขนส่งเอกชน */}
          <div className="border border-gray-200 rounded-lg p-4">
            <label className="flex items-center gap-2 mb-3">
              <input
                type="checkbox"
                checked={formData.shipping.methods.includes('courier')}
                onChange={(e) => {
                  const methods = e.target.checked
                    ? [...formData.shipping.methods, 'courier']
                    : formData.shipping.methods.filter(m => m !== 'courier');
                  setFormData(prev => ({
                    ...prev,
                    shipping: {
                      ...prev.shipping,
                      methods
                    }
                  }));
                }}
                className="w-5 h-5 text-green-500 rounded focus:ring-green-500"
              />
              <span className="font-semibold text-gray-700">บริษัทขนส่งเอกชน</span>
            </label>
            
            {formData.shipping.methods.includes('courier') && (
              <div className="ml-7 space-y-3">
                <div className="flex flex-wrap gap-3">
                  {['J&T', 'Flash', 'Kerry', 'DHL', 'FedEx', 'Nim Express'].map(company => (
                    <label key={company} className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={formData.shipping.courierCompanies.includes(company)}
                        onChange={(e) => {
                          const companies = e.target.checked
                            ? [...formData.shipping.courierCompanies, company]
                            : formData.shipping.courierCompanies.filter(c => c !== company);
                          setFormData(prev => ({
                            ...prev,
                            shipping: {
                              ...prev.shipping,
                              courierCompanies: companies
                            }
                          }));
                        }}
                        className="w-4 h-4 text-green-500 rounded focus:ring-green-500"
                      />
                      <span className="text-sm text-gray-700">{company}</span>
                    </label>
                  ))}
                </div>
                
                {/* ค่าจัดส่ง - บริษัทขนส่ง */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">{t('store.shippingFeeType')}</label>
                    <select
                      name="shipping.fees.courier.type"
                      value={formData.shipping.fees.courier.type}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                    >
                      <option value="fixed">ราคาคงที่</option>
                      <option value="rate">ตามน้ำหนัก/ระยะทาง</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">จำนวนเงิน (บาท)</label>
                    <input
                      type="number"
                      name="shipping.fees.courier.amount"
                      value={formData.shipping.fees.courier.amount}
                      onChange={handleInputChange}
                      min="0"
                      step="0.01"
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                      placeholder="0"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* นัดรับ */}
          <div className="border border-gray-200 rounded-lg p-4">
            <label className="flex items-center gap-2 mb-3">
              <input
                type="checkbox"
                checked={formData.shipping.methods.includes('pickup')}
                onChange={(e) => {
                  const methods = e.target.checked
                    ? [...formData.shipping.methods, 'pickup']
                    : formData.shipping.methods.filter(m => m !== 'pickup');
                  setFormData(prev => ({
                    ...prev,
                    shipping: {
                      ...prev.shipping,
                      methods
                    }
                  }));
                }}
                className="w-5 h-5 text-green-500 rounded focus:ring-green-500"
              />
              <span className="font-semibold text-gray-700">นัดรับ</span>
            </label>
            
            {formData.shipping.methods.includes('pickup') && (
              <div className="ml-7 space-y-3">
                <div>
                  <label className="block text-sm text-gray-700 mb-2">ที่อยู่</label>
                  <input
                    type="text"
                    name="shipping.pickupLocation"
                    value={formData.shipping.pickupLocation}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                    placeholder={t('store.addressForPickup')}
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-700 mb-2">เวลา</label>
                  <input
                    type="text"
                    name="shipping.pickupTime"
                    value={formData.shipping.pickupTime}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                    placeholder="เช่น 09:00 - 17:00"
                  />
                </div>
                <div className="bg-green-50 border border-green-200 rounded-lg p-2">
                  <p className="text-sm text-green-700">💡 นัดรับ: ฟรี</p>
                </div>
              </div>
            )}
          </div>

          {/* ฝากส่งกับรถพิเศษ */}
          <div className="border border-gray-200 rounded-lg p-4">
            <label className="flex items-center gap-2 mb-3">
              <input
                type="checkbox"
                checked={formData.shipping.methods.includes('special_vehicle')}
                onChange={(e) => {
                  const methods = e.target.checked
                    ? [...formData.shipping.methods, 'special_vehicle']
                    : formData.shipping.methods.filter(m => m !== 'special_vehicle');
                  setFormData(prev => ({
                    ...prev,
                    shipping: {
                      ...prev.shipping,
                      methods
                    }
                  }));
                }}
                className="w-5 h-5 text-green-500 rounded focus:ring-green-500"
              />
              <span className="font-semibold text-gray-700">ฝากส่งกับรถพิเศษ</span>
            </label>
            
            {formData.shipping.methods.includes('special_vehicle') && (
              <div className="ml-7 space-y-3">
                <div>
                  <label className="block text-sm text-gray-700 mb-2">{t('store.details')}</label>
                  <textarea
                    name="shipping.specialVehicleInfo"
                    value={formData.shipping.specialVehicleInfo}
                    onChange={handleInputChange}
                    rows="3"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                    placeholder={t('store.specialVehicleDetails')}
                  />
                </div>
                
                {/* ค่าจัดส่ง - รถพิเศษ */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">{t('store.shippingFeeType')}</label>
                    <select
                      name="shipping.fees.specialVehicle.type"
                      value={formData.shipping.fees.specialVehicle.type}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                    >
                      <option value="fixed">ราคาคงที่</option>
                      <option value="rate">ตามน้ำหนัก/ระยะทาง</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">จำนวนเงิน (บาท)</label>
                    <input
                      type="number"
                      name="shipping.fees.specialVehicle.amount"
                      value={formData.shipping.fees.specialVehicle.amount}
                      onChange={handleInputChange}
                      min="0"
                      step="0.01"
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                      placeholder="0"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ปุ่มบันทึก */}
        <div className="flex gap-4 pt-4 border-t">
          <button
            type="submit"
            disabled={loading}
            className="flex-1 bg-green-500 hover:bg-green-600 disabled:bg-gray-300 text-white font-semibold py-3 px-6 rounded-lg transition-colors"
          >
            {loading ? t('store.saving') : t('store.saveChanges')}
          </button>
          <button
            type="button"
            onClick={() => {
              // Reset form to original store data
              if (store) {
                setFormData({
                  name: store.name || '',
                  description: store.description || '',
                  category: store.category || '',
                  contact: {
                    phone: store.contact?.phone || '',
                    email: store.contact?.email || '',
                    lineId: store.contact?.lineId || '',
                    address: store.contact?.address || ''
                  },
                  logo: store.logo || '',
                  coverImage: store.coverImage || '',
                  payment: {
                    methods: store.payment?.methods || ['bank_transfer'],
                    bankAccounts: store.payment?.bankAccounts || [],
                    qrCodes: store.payment?.qrCodes || [],
                    cashOnPickup: store.payment?.cashOnPickup || { enabled: false, location: '', time: '' },
                    cashOnDelivery: store.payment?.cashOnDelivery || { enabled: false, courierCompanies: [] }
                  },
                  shipping: {
                    methods: store.shipping?.methods || [],
                    courierCompanies: store.shipping?.courierCompanies || [],
                    pickupLocation: store.shipping?.pickupLocation || '',
                    pickupTime: store.shipping?.pickupTime || '',
                    specialVehicleInfo: store.shipping?.specialVehicleInfo || '',
                    fees: {
                      courier: store.shipping?.fees?.courier || { type: 'fixed', amount: 0 },
                      pickup: store.shipping?.fees?.pickup || { type: 'fixed', amount: 0 },
                      specialVehicle: store.shipping?.fees?.specialVehicle || { type: 'fixed', amount: 0 }
                    }
                  }
                });
              }
            }}
            className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-100 text-gray-700 font-semibold transition-colors"
          >
            {t('common.cancel')}
          </button>
        </div>
      </form>

      {/* Video Call Settings */}
      {store && store.id && (
        <div className="mt-8">
          <VideoCallSettings storeId={store.id} packageType={currentPackage} />
        </div>
      )}

      {/* Package Selection Modal */}
      <PackageSelectionModal
        isOpen={showPackageModal}
        onClose={() => setShowPackageModal(false)}
        currentPackage={currentPackage}
        onSelectPackage={handlePackageSelect}
      />

      {/* Store Verification Form Modal */}
      {showVerificationForm && store && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b p-4 flex items-center justify-between z-10">
              <h2 className="text-xl font-bold text-gray-800">การตรวจสอบร้านค้า</h2>
              <button
                onClick={() => setShowVerificationForm(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-6">
              <StoreVerificationForm
                store={store}
                onClose={() => setShowVerificationForm(false)}
                onSuccess={() => {
                  setShowVerificationForm(false);
                  if (onUpdate) {
                    // Refresh store data
                    onUpdate(formData);
                  }
                }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StoreSettingsForm;

